import useCurrency from '@hooks/useCurrency';
import { Badge, Tooltip } from '@ui';
import { cx } from 'class-variance-authority';
import { round } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatInvoiceNumber } from '../i18n';
import PriceDataGrid from './PriceDataGrid';
import { BaseComponentProps } from './types';

interface Props extends BaseComponentProps {
    /** @deprecated */
    css?: any;
}

/** Price badge of an invoice row: type abbreviation, over-/underpayment delta and total, on one line. */
export const PriceComponent = ({ invoice, ...props }: Props) => {
    const { t, i18n } = useTranslation('invoice');
    const { getSymbol } = useCurrency();
    const price_data = invoice.price_data;

    // Difference between the sum of all payments and the invoice total (positive = overpaid).
    const paymentValue = useMemo(() => {
        if (!invoice.payments_sum_amount) return null;
        const value = round(
            invoice.payments_sum_amount - Number(invoice.price_data?.total?.replace('.', '').replace(',', '.') ?? 0),
            2
        );
        if (value >= 0) return '+ ' + formatInvoiceNumber(value, i18n.resolvedLanguage);
        return formatInvoiceNumber(value, i18n.resolvedLanguage);
    }, [i18n.resolvedLanguage, invoice]);

    // Badge colour by invoice state: cancelled > paid > overdue > neutral.
    const resolveColor = () => {
        if (!!invoice.cancelled_at) return 'warning';
        if (invoice.is_paid) return 'success';
        if (invoice.due_date ? moment(invoice.due_date).isBefore(moment()) : false) return 'error';
        return 'secondary';
    };

    return (
        <Tooltip
            placement="bottom"
            tooltip={
                invoice.cancelled_at ? (
                    <span>
                        {t('invoiceList.price.cancelledReason', { reason: invoice.cancellation_reason || '-' })}
                    </span>
                ) : (
                    <PriceDataGrid price_data={invoice.price_data} currencyCode={invoice.currency} />
                )
            }
        >
            <Badge size="sm" color={resolveColor()} className="tw-w-full tw-text-right" {...props}>
                <div
                    className={cx(
                        !!invoice.cancelled_at && '!tw-line-through',
                        'tw-flex tw-flex-row tw-flex-nowrap tw-items-center tw-justify-between tw-gap-x-3 tw-whitespace-nowrap'
                    )}
                >
                    <span>
                        {invoice.isCredit ? t('invoiceList.price.creditAbbr') : t('invoiceList.price.invoiceAbbr')}
                    </span>
                    <span className="tw-flex tw-flex-col tw-items-end tw-leading-tight">
                        {/* Invisible placeholder keeps the badge height identical for invoices without any payment. */}
                        <span className={cx('tw-text-xs tw-font-normal', !paymentValue && 'tw-invisible')}>
                            {paymentValue ?? '0'} {getSymbol(invoice.currency)}
                        </span>
                        <span>
                            {price_data?.total || '-'} {getSymbol(invoice.currency)}
                        </span>
                    </span>
                </div>
            </Badge>
        </Tooltip>
    );
};
